import { countryToCurrency } from "@/i18n/markets";
import { formatMoneyWithCurrency } from "@/lib/formatMoney";
import type { CarCardData, SearchParams } from "@/lib/api";

export function searchPriceBoundsParams(params: SearchParams): SearchParams {
  // Existing search endpoint and cache, but never the refined/current page.
  return { ...params, price_min: undefined, price_max: undefined, price_attribute: undefined, page: 1 };
}

/**
 * Search compares raw listing prices. A display preference is not their
 * denomination. Only a country-constrained result set can supply a currency;
 * conflicting listing evidence makes even that assumption unsafe.
 */
export function resolveSearchPriceCurrency(
  country: string | null | undefined,
  cars: Pick<CarCardData, "currencyCode">[],
): string | null {
  const expected = countryToCurrency(country);
  if (!expected) return null;
  if (cars.some(car => !car.currencyCode || car.currencyCode.toUpperCase() !== expected)) return null;
  return expected;
}

export function formatSearchPrice(amount: number, currency: string | null, locale: string): string {
  return currency
    ? formatMoneyWithCurrency(amount, currency, { locale, decimals: 0 })
    : amount.toLocaleString(locale, { maximumFractionDigits: 0 });
}

export function searchPriceFromCar(car: CarCardData, rentalType?: string): number | null {
  if (rentalType === "driving") return car.priceDriving;
  if (rentalType === "event") return car.priceEvent;
  if (rentalType === "chauffeured") return car.chauffeurMinPrice;
  // Match the DB fallback's existing waterfall, not card display-price rules.
  return car.priceDriving ?? car.priceEvent ?? car.chauffeurMinPrice;
}

export function getSearchPriceBounds(min?: number | null, max?: number | null, rentalType?: string) {
  const cap = rentalType === "chauffeured" ? 500 : 2000;
  const validMin = Number.isFinite(min) && min! >= 0 ? min! : 0;
  const validMax = Number.isFinite(max) && max! > 0 ? max! : cap;
  // Keep outliers from collapsing the usable range. The top is a sentinel
  // for an unbounded maximum, never a limit on discoverable inventory.
  const upper = Math.max(10, Math.min(cap, Math.ceil(validMax / 10) * 10));
  const lower = Math.min(Math.floor(validMin / 10) * 10, upper - 10);
  return { min: Math.max(0, lower), max: upper };
}

export function searchPriceRefinement(values: number[], min: number, max: number) {
  return [
    values[0] <= min ? undefined : values[0],
    values[1] >= max ? undefined : values[1],
  ] as const;
}